import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import SwimmerSearch from '../../components/admin/SwimmerSearch';
import { Plus, Loader2, Clock, Users as UsersIcon, Pencil, AlertTriangle } from 'lucide-react';

const ACTIVITIES = [
  { id: 'self_swimming', label: 'Self Swimming', color: 'bg-cyan-100 text-cyan-800', dot: 'bg-cyan-500' },
  { id: 'coaching_kids', label: 'Coaching (Kids)', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  { id: 'coaching_adults', label: 'Coaching (Adults)', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  { id: 'coaching_ladies', label: 'Coaching (Ladies)', color: 'bg-pink-100 text-pink-800', dot: 'bg-pink-500' },
  { id: 'free', label: 'Available / Free Slot', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
];
const actMeta = (id) => ACTIVITIES.find((a) => a.id === id) || ACTIVITIES[0];
const fmtTime = (t) => { if (!t) return ''; const h = Number(t.split(':')[0]); return `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`; };
const slotLabel = (b) => `${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}`;
const HOURS = Array.from({ length: 18 }, (_, i) => i + 5); // 5..22

const emptyForm = { name: '', activity_type: 'self_swimming', day_type: 'weekday', start_time: '05:00', end_time: '06:00', max_capacity: 40, coach_id: 'none' };

const BatchesPage = () => {
  const [batches, setBatches] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayType, setDayType] = useState('weekday');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(null);
  const [enrolled, setEnrolled] = useState([]);
  const [warn, setWarn] = useState(null); // { swimmer, message }
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get('/batches', { params: { active_only: true, limit: 100 } }),
        api.get('/admin/users', { params: { role: 'coach', limit: 100 } }).catch(() => ({ data: [] })),
      ]);
      setBatches(b.data);
      setCoaches(c.data);
    } catch (err) { toast({ title: 'Failed to load', description: formatApiError(err), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm({ ...emptyForm, day_type: dayType }); setOpen(true); };
  const openEdit = (b) => {
    setEditId(b.id);
    setForm({ name: b.name, activity_type: b.activity_type, day_type: b.day_type, start_time: b.start_time || '05:00', end_time: b.end_time || '06:00', max_capacity: b.max_capacity, coach_id: b.coach_id || 'none' });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim() || `${form.day_type === 'weekday' ? 'Weekday' : 'Weekend'} ${fmtTime(form.start_time)} – ${fmtTime(form.end_time)}`,
        activity_type: form.activity_type, day_type: form.day_type,
        start_time: form.start_time, end_time: form.end_time,
        max_capacity: Number(form.max_capacity) || 1,
        coach_id: form.coach_id === 'none' ? null : form.coach_id,
      };
      if (editId) await api.put(`/batches/${editId}`, payload);
      else await api.post('/batches', payload);
      toast({ title: editId ? 'Slot updated' : 'Slot added' });
      setOpen(false); load();
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const openManage = async (b) => {
    setManage(b);
    try { setEnrolled((await api.get(`/batches/${b.id}/swimmers`)).data); } catch { setEnrolled([]); }
  };

  const doEnroll = async (swimmer, force = false) => {
    try {
      const res = await api.post(`/batches/${manage.id}/enroll`, { swimmer_id: swimmer.id, force });
      toast({ title: res.data.message || 'Enrolled' });
      setWarn(null);
      openManage(manage); load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) { setWarn({ swimmer, message: err.response.data.detail }); }
      else { toast({ title: 'Cannot enroll', description: formatApiError(err), variant: 'destructive' }); }
    }
  };

  const unenroll = async (swimmerId) => {
    try { await api.post(`/batches/${manage.id}/unenroll`, { swimmer_id: swimmerId }); toast({ title: 'Removed' }); openManage(manage); load(); }
    catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>;

  const daySlots = batches.filter((b) => b.day_type === dayType).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

  return (
    <div data-testid="admin-batches">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pool Schedule</h1>
          <p className="text-gray-500 text-sm mt-1">Weekly timetable · activity per hour · enrollments</p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-teal-600 to-cyan-600" data-testid="new-batch-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Slot
        </Button>
      </div>

      <Tabs value={dayType} onValueChange={setDayType} className="mb-4">
        <TabsList data-testid="daytype-tabs">
          <TabsTrigger value="weekday" data-testid="tab-weekday">Weekdays (Mon–Fri)</TabsTrigger>
          <TabsTrigger value="weekend" data-testid="tab-weekend">Weekend (Sat–Sun)</TabsTrigger>
        </TabsList>
      </Tabs>

      {daySlots.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="text-center py-16 text-gray-500" data-testid="batches-empty">No slots for {dayType}. Add one.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="schedule-list">
          {daySlots.map((b) => {
            const m = actMeta(b.activity_type);
            const isCoaching = b.activity_type.startsWith('coaching');
            return (
              <Card key={b.id} className="border-0 shadow-sm" data-testid={`slot-${b.id}`}>
                <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 w-32 shrink-0">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-800">{slotLabel(b)}</span>
                  </div>
                  <Badge className={`${m.color} font-medium`}>{m.label}</Badge>
                  {b.coach_name && <Badge variant="secondary" className="text-xs">Coach: {b.coach_name}</Badge>}
                  <div className="flex-1" />
                  {isCoaching && <span className="text-xs text-gray-500 flex items-center gap-1"><UsersIcon className="h-3.5 w-3.5" />{b.current_count}/{b.max_capacity}</span>}
                  <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`edit-slot-${b.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => openManage(b)} data-testid={`manage-slot-${b.id}`}><UsersIcon className="h-3.5 w-3.5 mr-1" />Members</Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit slot dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="slot-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Slot' : 'Add Slot'}</DialogTitle>
            <DialogDescription>Configure the time, activity and capacity.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Day Type</Label>
                <Select value={form.day_type} onValueChange={(v) => setForm((p) => ({ ...p, day_type: v }))}>
                  <SelectTrigger data-testid="slot-daytype-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="weekday">Weekday</SelectItem><SelectItem value="weekend">Weekend</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Activity</Label>
                <Select value={form.activity_type} onValueChange={(v) => setForm((p) => ({ ...p, activity_type: v }))}>
                  <SelectTrigger data-testid="slot-activity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIVITIES.map((a) => (<SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start</Label>
                <Select value={form.start_time} onValueChange={(v) => { const h = Number(v.split(':')[0]); setForm((p) => ({ ...p, start_time: v, end_time: `${String(h + 1).padStart(2, '0')}:00` })); }}>
                  <SelectTrigger data-testid="slot-start-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map((h) => (<SelectItem key={h} value={`${String(h).padStart(2, '0')}:00`}>{fmtTime(`${h}:00`)}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>End</Label>
                <Select value={form.end_time} onValueChange={(v) => setForm((p) => ({ ...p, end_time: v }))}>
                  <SelectTrigger data-testid="slot-end-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map((h) => h + 1).map((h) => (<SelectItem key={h} value={`${String(h).padStart(2, '0')}:00`}>{fmtTime(`${h}:00`)}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Capacity</Label><Input type="number" value={form.max_capacity} onChange={(e) => setForm((p) => ({ ...p, max_capacity: e.target.value }))} data-testid="slot-capacity-input" /></div>
              <div className="space-y-2">
                <Label>Coach (optional)</Label>
                <Select value={form.coach_id} onValueChange={(v) => setForm((p) => ({ ...p, coach_id: v }))}>
                  <SelectTrigger data-testid="slot-coach-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{coaches.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Name (optional)</Label><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Auto-generated if blank" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-teal-600 to-cyan-600" data-testid="slot-save-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editId ? 'Save' : 'Add Slot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage enrollments dialog */}
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent data-testid="manage-batch-dialog">
          <DialogHeader>
            <DialogTitle>{manage && slotLabel(manage)} · {manage && actMeta(manage.activity_type).label}</DialogTitle>
            <DialogDescription>Enrolled ({enrolled.length}/{manage?.max_capacity}). Search to enroll a member.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <SwimmerSearch onSelect={(s) => doEnroll(s, false)} placeholder="Search member to enroll" />
            <div className="max-h-60 overflow-auto space-y-1">
              {enrolled.length === 0 ? <p className="text-sm text-gray-500 py-2">No members enrolled yet.</p> : enrolled.map((s) => (
                <div key={s.swimmer_id} className="flex items-center justify-between text-sm p-2 border border-gray-100 rounded" data-testid={`enrolled-${s.swimmer_id}`}>
                  <span>{s.name} <span className="text-gray-400 font-mono text-xs">{s.swimmer_id_code}</span></span>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600 text-xs" onClick={() => unenroll(s.swimmer_id)}>Remove</Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audience mismatch warning */}
      <Dialog open={!!warn} onOpenChange={(o) => !o && setWarn(null)}>
        <DialogContent data-testid="audience-warn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Check this slot</DialogTitle>
            <DialogDescription>{warn?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWarn(null)}>Cancel</Button>
            <Button onClick={() => doEnroll(warn.swimmer, true)} className="bg-amber-500 hover:bg-amber-600" data-testid="warn-confirm-btn">Enroll Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BatchesPage;
